/**
 * Registro del Service Worker.
 *
 * **Se registra siempre, no solo cuando hay recordatorios activos.** El Service Worker sirve a dos
 * cosas distintas: recibe los mensajes push del Requisito 25 y es el requisito que el navegador exige
 * para ofrecer la instalación de la aplicación web junto al manifiesto. La segunda no depende de
 * ningún permiso, así que el registro va en el arranque del SPA y no detrás de la acción de activar
 * los recordatorios. De ahí que este módulo esté separado de `push.ts`: uno lo llama el montaje y el
 * otro una acción de la persona.
 *
 * **No se registra en el servidor de desarrollo de Vite, y eso es lo único condicional.** El Service
 * Worker se construye aparte, con `vite.sw.config.ts`, y queda en `dist/sw.js`: en `vite dev` ese
 * archivo no existe, así que registrarlo dejaría un fallo en la consola en cada carga y ninguna otra
 * consecuencia. `wrangler pages dev`, que es el modo que reproduce el origen de producción, sirve
 * `dist/` ya construido y por lo tanto sí lo registra.
 *
 * **Ningún fallo del registro se propaga.** Un navegador sin soporte, un modo privado que lo
 * deshabilita o un `dist/sw.js` que no se pudo descargar dejan la aplicación funcionando igual, sin
 * recordatorios y sin instalación. Es la degradación que el criterio 25.2 ya prevé para el permiso
 * denegado, aplicada un paso antes.
 */

/**
 * Camino del archivo construido del Service Worker.
 *
 * Está en la raíz y no bajo `assets/` a propósito: el ámbito de un Service Worker no puede ser más
 * ancho que la carpeta desde la que se sirve, y hace falta que cubra la aplicación completa para que
 * un recordatorio pulsado pueda abrir cualquier vista. El nombre no lleva resumen de contenido, que es
 * la otra condición: la ruta tiene que ser estable para que el navegador reconozca la actualización
 * del mismo Service Worker en lugar de instalar uno nuevo al lado.
 */
export const RUTA_DEL_SERVICE_WORKER = '/sw.js';

/** Ámbito del registro: la aplicación completa. */
export const AMBITO_DEL_SERVICE_WORKER = '/';

/** Si el navegador tiene la interfaz de Service Worker. */
export function soportaServiceWorker(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/**
 * Registra el Service Worker, o devuelve nulo si no se pudo.
 *
 * Es idempotente por construcción: `register` con el mismo camino y el mismo ámbito devuelve el
 * registro que ya existe en lugar de crear otro, así que llamarla desde el montaje y otra vez desde la
 * activación de los recordatorios no instala dos.
 *
 * @returns el registro activo, o nulo si el navegador no lo soporta, si el archivo no se construyó en
 *   este modo, o si el registro falló
 */
export async function registrarServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!soportaServiceWorker() || !import.meta.env.PROD) {
    return null;
  }

  try {
    return await navigator.serviceWorker.register(RUTA_DEL_SERVICE_WORKER, {
      scope: AMBITO_DEL_SERVICE_WORKER,
      // Script clásico y no módulo: `vite.sw.config.ts` construye un paquete autocontenido en
      // formato de función inmediata, sin ninguna sentencia `import`, precisamente para no depender
      // del soporte de módulos en Service Workers, que es reciente en algunos navegadores.
      type: 'classic',
    });
  } catch {
    return null;
  }
}
